import React, { useContext } from 'react';
import { Card, Box, Button, Link, Grid } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import { AuthStatusContext, retrievePlan } from '@trp-individual-investor-adv-disc/adv-shared';
import FPSThemeWrapper from '../FPSThemeWrapper/FPSThemeWrapper';
import CardWithMedia from './Cards/CardWithMedia';
import CardAction from './Cards/CardAction'; 
import ExternalIcon from '../../images/External.svg';

import PageWrapper from '../PageWrapper/PageWrapper';
import './StatusHub.scss'
import IPDocCard from '../IPDocCard/IPDocCard';
import { confirmContentPurple, confirmActionContentPurple } from '../../utils/Content';

const StatusHub = (props) => {
  const { applicationConfig } = useContext(AuthStatusContext).state;

  const { error, status } = props;

  const isInternal = window.location.hostname.indexOf('.awstrp.net') !== -1;
    const statusData = {
        title: "Sorry, something went wrong.",
        content: "Thanks for your patience. Please try refreshing your page or check back later.",
        buttonText: "Refresh Page",
        buttonHref: window.location.href.replace('/error', ''),
        class: "hubStatusExpired",
        icon: ""
    };

    const getPlan = async () => {
        await retrievePlan(applicationConfig)
    };

    const cardActionOnclick = (id) => {
        if (id === 'view-plan') {
            getPlan()
        }
        return null;
    };

    const errorCallDetails = <p>
        If the problem continues, call&nbsp;
        <Link href="tel:+1-888-358-8490" onClick={event => event.preventDefault()} className='hubCardFooter'>
            1-888-358-8490
        </Link>&nbsp;for advisory support.
    </p>;

    const primary = status && status.client === 'PRIMARY';
    const currentUser = primary ? 'client' : 'coClient';
    const partner = primary ? 'coClient' : 'client';
    const partnerSubmitted = status && (
        (!status[`${partner}RequiresRASU`] && !status[`${partner}HasRAS`]) ||
        (status[`${partner}RequiresRASU`] && status[`${partner}Submitted`]) ||
        (!status[`${partner}RequiresRASU`] && status[`${partner}HasRAS`] && status.modelChange && status[`${partner}Activated`]) ||
        (!status[`${partner}RequiresRASU`] && status[`${partner}Activated`])
    );
    const partnerNotRequired = window.location.search && window.location.search.length > 0;
    const hasModelChange = status && status.modelChange;
    const hasInternalAccounts = status && status[`${currentUser}HasInternalAccounts`];
    const hasExternalAccounts = status && status[`${currentUser}HasExternalAccounts`];
    const roundTrip = status && (status.clientHasRAS || status.coClientHasRAS);
    const externalWaiting = !primary && status && status.jointExternalAccounts && !status.clientExternalSubmitted;

    const subText = `Please encourage your planning partner to submit their information. Their submission will allow ${hasModelChange ? 'the transition to your new recommended portfolio' : ''}${hasModelChange && hasInternalAccounts ? ' and ' : ''}${hasInternalAccounts ? 'the transfer of the internal accounts you selected into the Service' : ''} to continue.`;
    const processText = `
        <div>What happens next?</div>
        <ul>
            ${hasModelChange ?
                `<li>
                    The process to transition your advisory accounts into your new recommended portfolio will complete shortly after all household members have submitted
                </li>`
            : ''}
            ${hasInternalAccounts ?
                `<li>
                    The internal accounts that you chose to fund your advisory accounts will be transferred in-kind, to keep your assets in the market as long as possible.
                </li>
                <li>
                    Please note that during this time there may be a period of up to two days during which your current T. Rowe Price accounts, 
                    as well as your new advisory accounts, display a zero balance online. This is expected due to the steps required to implement your recommended portfolio across your advisory accounts.
                </li>`
            : ''}
            <li>
                You can always login to your dashboard to monitor your Retirement Advisory Service accounts. If you have any questions, please contact your advisor.
            </li>
        </ul>
    `;
    const externalProcessText = `
        ${hasModelChange || hasInternalAccounts ?
            `<div>
                <span class="material-icons materialCompleteOverride">check_circle</span>
                <strong>We're processing your ${hasModelChange ? 'model change' : ''}${hasModelChange && hasInternalAccounts ? ' and ' : ''}${hasInternalAccounts ? 'internal funding' : ''}.</strong>
            </div>
            <ul>
                <li>
                    The process ${hasModelChange ? 'to transition your advisory accounts into your new recommended portfolio ' : ''}
                    ${hasModelChange && hasInternalAccounts ? 'and ' : ''}
                    ${hasInternalAccounts ? 'to transfer the internal accounts you selected into the Service ' : ''}
                    will complete shortly after all household members have submitted.
                </li>
                ${hasInternalAccounts ? `
                    <li>
                        The internal accounts that you chose to fund your new advisory accounts will be transferred in-kind,
                        to keep your assets in the market as long as possible.
                    </li>
                    <li>
                        Please note that during this time there may be a period of up to two days during which your current T. Rowe Price accounts,
                        as well as your new advisory accounts, display a zero balance online.
                        This is expected due to the steps required to implement your recommended portfolio across your advisory accounts.
                    </li>
                ` : ''}
            </ul>`
        : ''}
        <div>
            <span class="material-icons materialNextOverride">check_circle</span>
            <strong>Transfer assets from external accounts.</strong>
        </div>
        <ul>
            <li>
                Provide external account numbers and additional transfer details for the accounts you identified during the review of your investment proposal.
            </li>
            <li>
                Electronically sign and upload a statement, dated within 12 months, for each external account you are transferring. 
            </li>
        </ul>
        ${externalWaiting ?
            "<div>You'll be able to proceed once your planning partner has completed their portion of the external transfer process.</div>"
        : ''}
    `;


    let cardMediaData;
    switch(true) {
        case hasExternalAccounts:
            cardMediaData = {
                header: `You're one step closer to completing your ${roundTrip ? 'updates.' : 'enrollment!'}`,
                cardButtonText: externalWaiting ? undefined : "Transfer External Accounts",
                id: "Transfer_External_Accounts",
                linkTo: `${document.location.origin}/personal-investing/tools/adv-nasu/external`,
                target: '_self',
                class: "hubStatusComplete",
                image: ExternalIcon,
                contactCard: true
            };
            break;
        case partnerSubmitted || partnerNotRequired:
            cardMediaData = confirmContentPurple.Confirm;
            break;
        case status && !(status[`${currentUser}HasAccounts`]) && !(status[`${partner}HasAccounts`]):
            cardMediaData = confirmContentPurple.StatusModel;
            break;
        default:
            cardMediaData = confirmContentPurple.StatusAccount;
    }

    return (
        <PageWrapper key="hubPageWrapper">
            <Grid container>
                {error ? (<Grid item xs={12}>
                    <Card className={`${statusData.class} hubCardMargin`}>
                        <div className='hubIconContainer'>
                            <ErrorIcon htmlColor="#EA6C23" className='hubErrorIcon' />
                        </div>
                        <div className='hubMainContent'>
                            <h3 className='hubLargeHeader'>{statusData.title}</h3>
                            <p>{statusData.content}</p>
                            {error ? errorCallDetails : null}
                            {statusData.buttonText ?
                                <Button variant="contained" color="primary" style={{ marginTop: '16px' }}
                                    onClick={() => {
                                        window.location.replace(statusData.buttonHref);
                                    }}
                                >
                                    {statusData.buttonText}
                                </Button>
                                : null}
                        </div>
                    </Card>
                </Grid>) :
                (
                    <FPSThemeWrapper>
                        <CardWithMedia
                            headerText={cardMediaData.header}
                            title={`Congratulations! ${partnerSubmitted ? "Your changes have been submitted." : "You've finished your part!"}!`}
                            secondaryText={!hasExternalAccounts && !partnerSubmitted && !partnerNotRequired ? subText : null}
                            smallSecondaryText={hasExternalAccounts ? externalProcessText : processText}
                            cardId={cardMediaData.id} 
                            onClick={() => cardActionOnclick(cardMediaData.id)}
                            href={cardMediaData.linkTo}
                            target={cardMediaData.target}
                            cardButtonText={cardMediaData.cardButtonText}
                            cardMedia={cardMediaData.image}
                            noImageInMobile />
                        <div className='cardActionContainer'>
                            {!(hasExternalAccounts || partnerSubmitted || partnerNotRequired) && confirmActionContentPurple.map(e =>
                                <Grid item xs={12} sm={12}>
                                    <Box pb={3}>
                                        <CardAction
                                            headerText={e.header}
                                            trademarked={e.trademarked}
                                            contentText={e.content}
                                            buttonId={`${e.id}-button`}
                                            cardId={e.id}
                                            onClick={() => cardActionOnclick(e.id)}
                                            href={e.linkTo}
                                            target={e.target}
                                            cardButtonText={e.buttonText}
                                            cardLinkText={e.linkText}
                                            cardIcon={e.image}
                                            iconAlt='e.id-icon'
                                            maxWidth="sm" />
                                    </Box>
                                </Grid>
                            )}
                        </div>
                    </FPSThemeWrapper>
                )}
                {isInternal && (
                    <Grid item xs={12}>
                        <div className='hubCardMargin'>
                            <IPDocCard />
                        </div>
                    </Grid>
                )}
            </Grid>
        </PageWrapper>
    );
};

export default StatusHub;
